'use client'

import { Typography, styled } from '@mui/material'
import HeadingWithParagraphs from '@/UI/Paragraph/HeadingWithParagraphs'
import HeadingWithAnchor from '@/UI/Heading/HeadingWithAnchor'
import PaddedGrid from '@/UI/Layout/Grid/PaddedGrid'
import Link from 'next/link'

const ListItem = styled('ul')(({ theme }) => ({
  li: {
    marginBottom: theme.spacing(2)
  }
}))

const PageContent = () => {
  return (
    <>
      <Typography variant="h1" align="center">
        Initial Disclosure Document
      </Typography>
      <PaddedGrid columns={12}>
        <Typography variant="body1">
          Thank you for choosing Zuto. Here is some information that will be helpful for you when
          progressing with your vehicle finance to help you decide if our service is right for you.
          To make it simple, we have added everything into one place to answer any questions you
          might have.
        </Typography>
        <Typography variant="body1" sx={{ mb: (theme) => theme.spacing(7) }}>
          Once you&apos;ve decided to go ahead with vehicle finance through Zuto, it&apos;s
          important that you read the following information alongside your pre-contract information.
        </Typography>
        <HeadingWithAnchor
          sectionId="who-are-we-regulated-by"
          title="1. Who are we regulated by?"
        />
        <ListItem sx={{ mb: (theme) => theme.spacing(7) }}>
          <li>
            <Typography variant="body1">
              Zuto Ltd is authorised and regulated by the Financial Conduct Authority for credit
              broking activity. Our FCA firm reference number is 452589.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              Zuto&apos;s company registration number is 05722976. Our registered address is
              Winterton House, Winterton Way, Macclesfield, Cheshire, SK11 0LP.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">
              We are a registered data controller with the Information Commissioners Office (ICO),
              registration number Z9481655.
            </Typography>
          </li>
        </ListItem>
        <HeadingWithParagraphs
          title="2. What services do we provide?"
          paragraphs={[
            "Zuto is a credit broker and not a lender. Throughout the process, your Car Buying Expert will ask you for information that will help us identify your needs and present a selection of products that are relevant. Please note, we are not financial advisors, and we don't offer financial advice - you must decide whether the finance product is right for you."
          ]}
          styleOverrides={{ marginBottom: 0 }}
          sectionId="what-services-do-we-provide"
          hasAnchor
        />
        <ListItem sx={{ mb: (theme) => theme.spacing(7) }}>
          <li>Conditional sale</li>
          <li>Hire purchase</li>
          <li>Personal contract purchase</li>
          <li>Personal loan</li>
        </ListItem>
        <HeadingWithAnchor
          sectionId="who-can-apply"
          title="3. Who can apply for finance with us?"
        />
        <ListItem sx={{ mb: (theme) => theme.spacing(7) }}>
          <li>All finance is subject to status and terms and conditions apply</li>
          <li>Over 18&apos;s only</li>
          <li>You must be a UK resident and hold a UK bank account</li>
        </ListItem>
        <HeadingWithParagraphs
          title="4. What products do we offer?"
          paragraphs={[
            "As we're a broker, we can introduce you to a group of lenders on our panel who provide credit and may be able to offer you finance for your vehicle purchase. Our role as a credit broker is to work with you to determine which product best suits your status and requirements. Each credit provider / lender offers different products and interest rates based on your individual circumstances."
          ]}
          sectionId="what-services-do-we-provide"
          hasAnchor
        />
        <HeadingWithParagraphs
          title="5. Commission Arrangements"
          paragraphs={[
            "At Zuto, we don't charge you a fee for our services. We will receive a commission payment from our lenders for introducing you to them. The amount we receive will vary depending on the lender, product or amount borrowed. The commission received is either a fixed fee, or a fixed percentage of the total loan. Details on the amount of commission are available on request. Specific details of the amount and nature of commission earned by Zuto will be shared with you during your application process."
          ]}
          sectionId="commission-arrangements"
          hasAnchor
        />
        <HeadingWithParagraphs
          title="6. Lender fees"
          paragraphs={[
            'Some lenders may charge an acceptance fee which would be added to the finance amount. They may also charge an Option to Purchase Fee, which is a fee charged by the lender to legally transfer ownership of the vehicle to you at the end of your agreement. If either of these fees apply, we will always explain them to you before proceeding.'
          ]}
          sectionId="lender-fees"
          hasAnchor
        />
        <HeadingWithAnchor sectionId="contact-us" title="7. Contact us" />
        <Typography variant="body1" mb={(theme) => theme.spacing(7)}>
          If you&apos;d like any further information or have any questions about the information
          within this document, or your finance agreement please contact a member of our team on{' '}
          <Link href="tel: 01625 619944" role="link">
            01625 619944
          </Link>
        </Typography>
        <HeadingWithParagraphs
          title="8. What to do if you have a complaint"
          paragraphs={[
            "We're committed to providing you with the best possible products and service but sometimes we know things can go wrong. If you're not happy or feel the need to make a complaint, you can do so:"
          ]}
          sectionId="what-to-do-if-you-have-a-complaint"
          styleOverrides={{ mb: 0 }}
          hasAnchor
        />
        <ListItem sx={{ mb: (theme) => theme.spacing(5) }}>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By Post: </strong>
            </Typography>
            <address>
              Customer Care, Zuto Limited, Winterton House, Winterton Way, Macclesfield, SK11 0LP
            </address>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By Phone: </strong>
            </Typography>
            <Link href="tel:01625 619 944" role="link">
              01625 619 944
            </Link>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By Email: </strong>
            </Typography>
            <Link href="mailto:customercare@zuto.com" role="link">
              customercare@zuto.com
            </Link>
          </li>
        </ListItem>
        <Typography variant="body1">
          If you&apos;re not able to settle your complaint with us, you have the right to refer any
          unresolved complaint to the Financial Ombudsman Service. This needs to be done within six
          months of the date you receive your final response letter. You can contact them:
        </Typography>
        <ListItem sx={{ mb: (theme) => theme.spacing(7) }}>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By Post: </strong>
            </Typography>
            <address>The Financial Ombudsman Service, Exchange Tower, London, E14 9SR.</address>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By Phone: </strong>
            </Typography>
            <Link href="tel:0800 023 4567" role="link">
              0800 023 4567
            </Link>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By Email: </strong>
            </Typography>
            <Link href="mailto:complaint.info@financial-ombudsman.org.uk" role="link">
              complaint.info@financial-ombudsman.org.uk
            </Link>
          </li>
        </ListItem>

        <HeadingWithAnchor
          sectionId="personal-data"
          title="9. Your Data Protection Rights and How to Complain"
        />
        <Typography variant="body1" mb={(theme) => theme.spacing(5)}>
          We take the privacy and security of your personal information seriously. If you are
          unhappy with the way we have collected, used, or handled your personal data, you have the
          right to raise a complaint with us. Data protection complaints are handled in line with
          applicable data protection laws, including the UK GDPR, the Data Protection Act 2018, and
          the Data (Use and Access) Act 2025, which may differ from our standard complaints process.
        </Typography>
        <Typography variant="titleMedium" component="h4" mb={(theme) => theme.spacing(4)}>
          What counts as a data protection complaint?
        </Typography>
        <Typography variant="body1">
          A data protection complaint includes any concern you have about how we handle your
          personal information. This might include, but is not limited to:
        </Typography>
        <ListItem sx={{ mb: (theme) => theme.spacing(5) }}>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              how or why we collected your information
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              how or why we use or share your data
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              your experience of requesting access to, or deletion of, your personal data
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              concerns about the accuracy of information we hold about you
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              any unauthorised use or disclosure of your personal data
            </Typography>
          </li>
        </ListItem>
        <Typography variant="titleMedium" component="h4" mb={(theme) => theme.spacing(4)}>
          How to raise a complaint
        </Typography>
        <Typography variant="body1" mb={(theme) => theme.spacing(4)}>
          If you have a concern about how we have handled your personal information, we encourage
          you to contact us in the first instance so we can try to resolve it for you.
        </Typography>
        <Typography variant="body1">
          Where a complaint is made through social media or another insecure public channel, we will
          ask you to continue the complaint through a more secure method to protect your data.
        </Typography>
        <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By Post:</strong>
            </Typography>
            <address>
              Customer Care, Zuto Limited, Winterton House, Winterton Way, Macclesfield, SK11 0LP
            </address>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By Phone:</strong>
            </Typography>
            <Link href="tel:01625619944" role="link">
              01625 619 944
            </Link>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By Email:</strong>{' '}
              <Link href="mailto:datarequests@zuto.com">datarequests@zuto.com</Link>
            </Typography>
          </li>
        </ListItem>
        <Typography variant="body1" mb={(theme) => theme.spacing(5)}>
          We aim to acknowledge your complaint within 30 days and will keep you informed throughout
          the process.
        </Typography>
        <Typography variant="titleMedium" component="h4" mb={(theme) => theme.spacing(4)}>
          Taking your complaint further
        </Typography>
        <Typography variant="body1" mb={(theme) => theme.spacing(4)}>
          If you are not satisfied with our response, you have the right to escalate your complaint
          to the Information Commissioner&apos;s Office (ICO), the UK&apos;s independent authority
          for data protection matters.
        </Typography>
        <Typography variant="body1">
          The Information Commissioner&apos;s Office can be contacted using any of the following
          methods:
        </Typography>
        <ListItem sx={{ mb: (theme) => theme.spacing(4) }}>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>In writing:</strong> Information Commissioner&apos;s Office, Wycliffe House,
              Water Lane, Wilmslow, Cheshire, SK9 5AF
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>By phone:</strong> <Link href="tel:03031231113">0303 123 1113</Link>
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>Website:</strong>{' '}
              <Link href="https://ico.org.uk/" role="link" target="_blank">
                https://ico.org.uk/
              </Link>
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 0 }}>
              <strong>Email:</strong>{' '}
              <Link href="mailto:icocasework@ico.org.uk" role="link">
                icocasework@ico.org.uk
              </Link>
            </Typography>
          </li>
        </ListItem>
        <Typography variant="body1" mb={(theme) => theme.spacing(5)}>
          A copy of the Information Commissioner&apos;s Office&apos;s data protection complaints
          process – setting out further details of the work they do – can be found on their website
          at{' '}
          <Link
            href="https://ico.org.uk/make-a-complaint/data-protection-complaints/"
            role="link"
            target="_blank"
          >
            https://ico.org.uk/make-a-complaint/data-protection-complaints/
          </Link>
          .
        </Typography>

        <HeadingWithAnchor sectionId="personal-data" title="10. Personal Data" />
        <Typography variant="body1" mb={(theme) => theme.spacing(7)}>
          Your personal data is being processed by Zuto Ltd. The data we collect and how we process
          it is set out in more detail in our Privacy Policy which can be seen here:{' '}
          <Link href="https://www.zuto.com/privacy/" role="link">
            https://www.zuto.com/privacy
          </Link>
        </Typography>
        <HeadingWithAnchor
          sectionId="discretionary-commission-arrangements"
          title="11. Discretionary Commission Arrangements"
        />
        <Typography variant="body1" mb={(theme) => theme.spacing(4)}>
          The FCA is currently undertaking a review of historic discretionary commission
          arrangements (DCA) within the motor finance sector and, to support this, they have made
          some changes to complaints rules relating to DCA. The key change that could impact our
          customers when making a complaint is the implementation of a “pause” to the normal 8-week
          response timescales that all firms, including Zuto, have to respond to complaints that
          relate to DCA. During this pause, we will continue to investigate any complaint relating
          to DCA, but you may not receive a final response to your complaint until the FCA has
          concluded their investigation. Under current rules we are not expected to issue a final
          response until after 31st May 2026.
        </Typography>
        <Typography variant="body1" mb={(theme) => theme.spacing(7)}>
          Further details in regards to what this means for you, your complaint and your FOS rights
          can be found here:{' '}
          <Link
            href="https://www.fca.org.uk/consumers/car-finance-complaints"
            target="_blank"
            role="link"
          >
            FCA - Car Finance Claims
          </Link>
        </Typography>
        <Typography variant="body1">Last updated: 20th February 2026</Typography>
      </PaddedGrid>
    </>
  )
}

export default PageContent
